import { useCallback, useMemo } from 'react';

import {
  useDropTelegramLinkMutation,
  useNotificationPrefsQuery,
  useSaveNotificationPrefsMutation,
  useStartTelegramLinkMutation,
} from 'gqlhooks';

export interface INotificationKindOption {
  key: string;
  label: string;
  enabled: boolean;
}

export interface IRobotRuleOption extends INotificationKindOption {
  /** Включено ли правило на сервере вообще. */
  available: boolean;
}

export interface INotificationPrefs {
  soundEnabled: boolean;
  pollSeconds: number;
  repeatAfterMinutes: number;
  quietFrom: string | null;
  quietTo: string | null;
  enabledKinds: string[];
  availableKinds: INotificationKindOption[];
  telegramEnabled: boolean;
  telegramLinked: boolean;
  telegramUsername: string;
  telegramBlockedAt: string | null;
  telegramAvailable: boolean;
  robotRules: string[];
  availableRobotRules: IRobotRuleOption[];
}

export interface INotificationPrefsInput {
  soundEnabled?: boolean;
  repeatAfterMinutes?: number;
  quietFrom?: string | null;
  quietTo?: string | null;
  clearQuietHours?: boolean;
  enabledKinds?: string[];
  telegramEnabled?: boolean;
  robotRules?: string[];
}

export interface ITelegramLink {
  deepLink: string;
  expiresAt: string;
}

export interface IUseNotificationPrefs {
  prefs: INotificationPrefs | null;
  loading: boolean;
  /** Запрос отказал: экран обязан сказать это, а не висеть в «Загрузка…». */
  error: boolean;
  saving: boolean;
  save: (input: INotificationPrefsInput) => Promise<boolean>;
  startTelegramLink: () => Promise<ITelegramLink | null>;
  dropTelegramLink: () => Promise<void>;
  refetch: () => void;
  lastError: string | null;
}

/** Настройки уведомлений сотрудника (ТЗ 0068). */
export const useNotificationPrefs = (skip = false): IUseNotificationPrefs => {
  const { data, loading, error, refetch } = useNotificationPrefsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const [
    saveMutation,
    { loading: saving, data: saved },
  ] = useSaveNotificationPrefsMutation();
  const [startLink] = useStartTelegramLinkMutation();
  const [dropLink] = useDropTelegramLinkMutation();

  const raw = data?.staffNotificationSettings;

  const prefs = useMemo<INotificationPrefs | null>(
    () =>
      raw
        ? {
            soundEnabled: raw.soundEnabled,
            pollSeconds: raw.pollSeconds,
            repeatAfterMinutes: raw.repeatAfterMinutes,
            quietFrom: raw.quietFrom ?? null,
            quietTo: raw.quietTo ?? null,
            enabledKinds: [...raw.enabledKinds],
            availableKinds: raw.availableKinds.map((kind) => ({
              key: kind.key,
              label: kind.label,
              enabled: kind.enabled,
            })),
            telegramEnabled: raw.telegramEnabled,
            telegramLinked: raw.telegramLinked,
            telegramUsername: raw.telegramUsername,
            telegramBlockedAt: raw.telegramBlockedAt ?? null,
            telegramAvailable: raw.telegramAvailable,
            robotRules: [...raw.robotRules],
            availableRobotRules: raw.availableRobotRules.map((rule) => ({
              key: rule.key,
              label: rule.label,
              enabled: rule.enabled,
              available: rule.available,
            })),
          }
        : null,
    [raw],
  );

  const save = useCallback(
    async (input: INotificationPrefsInput) => {
      const result = await saveMutation({ variables: { input } });
      return Boolean(result.data?.updateStaffNotificationSettings?.success);
    },
    [saveMutation],
  );

  const startTelegramLink = useCallback(async () => {
    const result = await startLink();
    const payload = result.data?.startTelegramLink;
    if (!payload?.success || !payload.link) return null;
    return {
      deepLink: payload.link.deepLink,
      expiresAt: payload.link.expiresAt,
    };
  }, [startLink]);

  const dropTelegramLink = useCallback(async () => {
    await dropLink();
    await refetch();
  }, [dropLink, refetch]);

  const lastError =
    saved?.updateStaffNotificationSettings?.customErrors?.[0]?.message ?? null;

  return {
    prefs,
    loading,
    error: Boolean(error),
    saving,
    save,
    startTelegramLink,
    dropTelegramLink,
    refetch: () => {
      refetch();
    },
    lastError,
  };
};
